import type { CambioFieldLog, EvaluacionAdminResponse } from '@/types/evaluacionAdmin';
import type {
  AsesoriaEnCurso,
  AsesoriaPendiente,
  DashboardAsesorEmpresasResponse,
  DashboardAsesoresResponse,
  DashboardEmpresaResponse,
  DashboardEmpresasResponse,
  DashboardGlobalResponse,
  DashboardNormasDetalleResponse,
  IndicadoresResumen,
  TendenciaMes,
} from '@/types/types';
import { api } from './api';

export interface AprobarAsesoriaResponse {
  success: boolean;
  data: {
    message: string;
    actaUrl: string | null;
  };
}

export interface RechazarAsesoriaResponse {
  success: boolean;
  data: {
    message: string;
    motivo: string | null;
  };
}

export interface ElementoBloqueante {
  tipo: 'EVIDENCIA' | 'AUDIO' | 'REQUISITO';
  id: number | string;
  nombre: string;
}

export interface MarcarAutoAprobarResponse {
  success: boolean;
  data?: { yaAprobable: boolean; marcada?: boolean };
  message?: string;
  elementos?: ElementoBloqueante[];
}

/** Resultado de simular una aprobación (modo debug) — no persiste ningún cambio, solo valida. */
export interface SimularAprobarResponse {
  success: boolean;
  simulacion?: true;
  ok?: boolean;
  motivo?: 'RECHAZOS' | 'PENDIENTES';
  /** true cuando la aprobación real caería en preaprobación (autoAprobar) por haber una asesoría
   * anterior de la empresa sin finalizar — ya no es un bloqueo, solo un dato informativo. */
  preaprobaria?: true;
  mensaje?: string;
  elementos?: ElementoBloqueante[];
  message?: string;
}

export interface DesmarcarAutoAprobarResponse {
  success: boolean;
  data: { message: string };
}

export interface ProyeccionNtc {
  ntcId: number;
  nombre: string;
  porcentaje: number;
  totalC: number;
  totalCP: number;
  totalNC: number;
  totalNA: number;
}

export interface ProyeccionCapa {
  cumplimientoGlobal: number;
  horasPct: number;
  horasAcumuladas: number;
  porNtc: ProyeccionNtc[];
}

export interface ProyeccionCumplimientoResponse {
  success: boolean;
  data: {
    aprobado: ProyeccionCapa;
    conCorrecciones: ProyeccionCapa;
    proyectado: ProyeccionCapa;
  };
}

export const dashboardService = {
  async getGlobal(): Promise<DashboardGlobalResponse> {
    return api.get<DashboardGlobalResponse>('/dashboard');
  },

  async getNormasDetalle(): Promise<DashboardNormasDetalleResponse> {
    return api.get<DashboardNormasDetalleResponse>('/dashboard/normas');
  },

  async getAsesores(): Promise<DashboardAsesoresResponse> {
    return api.get<DashboardAsesoresResponse>('/dashboard/asesores');
  },

  async getEmpresasByAsesor(asesorId: string): Promise<DashboardAsesorEmpresasResponse> {
    return api.get<DashboardAsesorEmpresasResponse>(`/dashboard/asesores/${asesorId}/empresas`);
  },

  async getEmpresa(nit: string): Promise<DashboardEmpresaResponse> {
    return api.get<DashboardEmpresaResponse>(`/dashboard/empresa/${nit}`);
  },

  async getEmpresas(): Promise<DashboardEmpresasResponse> {
    return api.get<DashboardEmpresasResponse>('/dashboard/empresa');
  },

  async getProyeccionCumplimiento(nit: string): Promise<ProyeccionCumplimientoResponse> {
    return api.get<ProyeccionCumplimientoResponse>(`/dashboard/empresa/${nit}/proyeccion`);
  },

  async getAsesoriasByEmpresa(nit: string): Promise<{
    success: boolean;
    asesorias: Array<{
      asesoriaId: number;
      estado: string;
      fechaInicio: string;
      fechaFin: string | null;
      objetivo: string;
      horas: number;
      recomendaciones: string | null;
      rechazos: number;
    }>;
  }> {
    return api.get<{
      success: boolean;
      asesorias: Array<{
        asesoriaId: number;
        estado: string;
        fechaInicio: string;
        fechaFin: string | null;
        objetivo: string;
        horas: number;
        recomendaciones: string | null;
        rechazos: number;
      }>;
    }>(`/dashboard/empresas/${nit}/asesorias`);
  },

  async getPendientesVerificacion(): Promise<{ success: boolean; data: AsesoriaPendiente[] }> {
    return api.get<{ success: boolean; data: AsesoriaPendiente[] }>('/dashboard/asesorias/pendientes');
  },

  async getAsesoriasResueltas(): Promise<{ success: boolean; data: AsesoriaPendiente[] }> {
    return api.get<{ success: boolean; data: AsesoriaPendiente[] }>('/dashboard/asesorias/resueltas');
  },

  async aprobarAsesoria(asesoriaId: number): Promise<AprobarAsesoriaResponse> {
    return api.post<AprobarAsesoriaResponse>(`/dashboard/asesorias/${asesoriaId}/aprobar`, {});
  },

  /** Modo debug: valida si la aprobación real pasaría o no, sin persistir ningún cambio. */
  async simularAprobarAsesoria(asesoriaId: number): Promise<SimularAprobarResponse> {
    return api.post<SimularAprobarResponse>(`/dashboard/asesorias/${asesoriaId}/simular-aprobar`, {});
  },

  /**
   * Modo debug: valida si el flujo completo de "Evaluar Asesoría" (reasignación) pasaría, sin
   * persistir ningún cambio. A diferencia de `simularAprobarAsesoria`, tiene en cuenta que ese
   * flujo primero limpia los rechazos vigentes (evidencias/audios se eliminan, requisitos/campos
   * de terna se revierten y quedan aprobados) antes de aprobar — así que no reporta esos rechazos
   * como bloqueo si la asesoría está en ese escenario.
   */
  async simularEvaluarAsesoria(asesoriaId: number): Promise<SimularAprobarResponse> {
    return api.post<SimularAprobarResponse>(`/dashboard/asesorias/${asesoriaId}/simular-evaluar`, {});
  },

  async marcarAutoAprobar(asesoriaId: number): Promise<MarcarAutoAprobarResponse> {
    return api.post<MarcarAutoAprobarResponse>(`/dashboard/asesorias/${asesoriaId}/auto-aprobar`, {});
  },

  async desmarcarAutoAprobar(asesoriaId: number): Promise<DesmarcarAutoAprobarResponse> {
    return api.delete<DesmarcarAutoAprobarResponse>(`/dashboard/asesorias/${asesoriaId}/auto-aprobar`);
  },

  async rechazarAsesoria(asesoriaId: number, motivo?: string, permiteSubirEvidenciasNuevas?: boolean): Promise<RechazarAsesoriaResponse> {
    return api.post<RechazarAsesoriaResponse>(`/dashboard/asesorias/${asesoriaId}/rechazar`, { motivo, permiteSubirEvidenciasNuevas });
  },

  async getEvaluacionAsesoria(asesoriaId: number): Promise<EvaluacionAdminResponse> {
    return api.get<EvaluacionAdminResponse>(`/dashboard/asesorias/${asesoriaId}/evaluaciones`);
  },

  async getEvaluacionActual(nit: string): Promise<EvaluacionAdminResponse> {
    return api.get<EvaluacionAdminResponse>(`/empresas/${nit}/evaluacion-actual`);
  },

  async getAsesoriasEnCurso(): Promise<{ success: boolean; data: AsesoriaEnCurso[] }> {
    return api.get<{ success: boolean; data: AsesoriaEnCurso[] }>('/dashboard/asesorias/en-curso');
  },

  async getIndicadoresResumen(): Promise<{ success: boolean; data: IndicadoresResumen }> {
    return api.get<{ success: boolean; data: IndicadoresResumen }>('/dashboard/indicadores/resumen');
  },

  async downloadExcelReport(): Promise<Blob> {
    return api.getBlob('/reports/excel/consolidado');
  },

  async downloadEmpresaExcelReport(nit: string): Promise<Blob> {
    return api.getBlob(`/reports/excel/empresa/${nit}`);
  },

  async downloadAsesoriaExcelReport(asesoriaId: number): Promise<Blob> {
    return api.getBlob(`/reports/excel/asesoria/${asesoriaId}`);
  },

  async downloadHorasAsesoresExcelReport(): Promise<Blob> {
    return api.getBlob('/reports/excel/horas-asesores');
  },

  async downloadConsolidadoPorAsesorReport(): Promise<Blob> {
    return api.getBlob('/reports/excel/consolidado-por-asesor');
  },

  async getTrend(): Promise<{ success: boolean; data: TendenciaMes[] }> {
    return api.get<{ success: boolean; data: TendenciaMes[] }>('/dashboard/trend');
  },

  async revisarEvidenciasAsesoria(
    asesoriaId: number,
    evaluaciones: { evidenciaId: number; estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string; requiereResubir?: boolean }[]
  ): Promise<{ success: boolean; asesoriaEstado?: string }> {
    return api.patch(`/dashboard/asesorias/${asesoriaId}/evidencias`, { evaluaciones });
  },

  async revisarAudiosAsesoria(
    asesoriaId: number,
    evaluaciones: { audioId: number; estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string; requiereResubir?: boolean }[]
  ): Promise<{ success: boolean; asesoriaEstado?: string }> {
    return api.patch(`/dashboard/asesorias/${asesoriaId}/revisar-audios`, { evaluaciones });
  },

  async resolverRechazosMatrizPorReasignacion(asesoriaId: number): Promise<{ success: boolean; resueltos: number }> {
    return api.patch(`/dashboard/asesorias/${asesoriaId}/resolver-rechazos-matriz-reasignacion`, {});
  },

  async revisarRequisitosAsesoria(
    asesoriaId: number,
    evaluaciones: { evaluacionRequisitoId: number; estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string }[]
  ): Promise<{ success: boolean; asesoriaEstado?: string }> {
    return api.patch(`/dashboard/asesorias/${asesoriaId}/revisar-requisitos`, { evaluaciones });
  },

  async revisarTernasCamposAsesoria(
    asesoriaId: number,
    evaluaciones: {
      evaluacionTernaId: number;
      campo: 'observaciones' | 'planMejora' | 'fechaImplementacion';
      estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO';
      observacion?: string;
    }[]
  ): Promise<{ success: boolean; asesoriaEstado?: string }> {
    return api.patch(`/dashboard/asesorias/${asesoriaId}/revisar-ternas-campos`, { evaluaciones });
  },

  async getCambios(asesoriaId: number): Promise<CambioFieldLog[]> {
    const res = await api.get<{ success: boolean; cambios: CambioFieldLog[] }>(`/dashboard/asesorias/${asesoriaId}/cambios`);
    return res.cambios;
  },

  async getAsesoriasActivas(nit: string): Promise<{
    success: boolean;
    asesorias: Array<{
      asesoriaId: number;
      estado: string;
      objetivo: string;
      fechaInicio: string;
      horas: number;
      orden: number | null;
    }>;
  }> {
    return api.get(`/empresas/${nit}/asesorias/activas`);
  },

  async getHistorialAsesorias(nit: string): Promise<{
    success: boolean;
    asesorias: Array<{
      asesoriaId: number;
      estado: string;
      autoAprobar?: boolean;
      objetivo: string;
      fechaInicio: string;
      fechaFin: string | null;
      horas: number;
      orden: number | null;
      rechazos: number;
      recomendaciones: string | null;
      cumplimientoGeneral: number | null;
      compromisos: string | null;
      conclusiones: string | null;
      actaLink: string | null;
      audios: Array<{ duracionSeg: number }>;
    }>;
  }> {
    return api.get(`/empresas/${nit}/asesorias/historial`);
  },

  async getRevisionHistorial(
    nit: string,
    asesoriaId: number
  ): Promise<{
    success: boolean;
    historial: Array<{
      id: number;
      asesoriaId: number;
      elementoTipo: string;
      elementoId: string | null;
      elementoNombre: string | null;
      estado: string;
      observacion: string | null;
      adminId: number | null;
      adminNombre: string | null;
      fecha: string;
    }>;
  }> {
    return api.get(`/empresas/${nit}/asesorias/${asesoriaId}/revision-historial`);
  },

  async solicitarAccesoArchivo(tipo: 'evidencia' | 'audio', id: number, email: string): Promise<{ success: boolean; message: string }> {
    return api.post('/dashboard/solicitar-acceso', { tipo, id, email });
  },

  /**
   * Ubicación mínima de una asesoría por su ID, sin requerir el NIT de antemano. Pensado para
   * cuando el admin llega por URL directa a una asesoría que ya no está en /pendientes (porque
   * quedó preaprobada o ya fue resuelta) — permite redirigir al historial en vez de un 404 genérico.
   */
  async getUbicacionAsesoria(
    asesoriaId: number
  ): Promise<{ success: boolean; asesoriaId: number; empresaNit: string; estado: string; autoAprobar: boolean }> {
    return api.get(`/dashboard/asesorias/${asesoriaId}/ubicacion`);
  },
};
